import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getActiveSeason,
  getMlbPool,
  getMyMlbRosters,
  getMlbPlayers,
  validateMlbSwap,
  applyMlbSwap,
} from "../api/mlbHrDerby";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

const MLB_ABBREVS = {
  "Arizona Diamondbacks": "ARI", "Atlanta Braves": "ATL", "Baltimore Orioles": "BAL",
  "Boston Red Sox": "BOS", "Chicago Cubs": "CHC", "Chicago White Sox": "CWS",
  "Cincinnati Reds": "CIN", "Cleveland Guardians": "CLE", "Colorado Rockies": "COL",
  "Detroit Tigers": "DET", "Houston Astros": "HOU", "Kansas City Royals": "KC",
  "Los Angeles Angels": "LAA", "Los Angeles Dodgers": "LAD", "Miami Marlins": "MIA",
  "Milwaukee Brewers": "MIL", "Minnesota Twins": "MIN", "New York Mets": "NYM",
  "New York Yankees": "NYY", "Oakland Athletics": "OAK", "Athletics": "ATH",
  "Sacramento Athletics": "ATH", "Philadelphia Phillies": "PHI", "Pittsburgh Pirates": "PIT",
  "San Diego Padres": "SD", "San Francisco Giants": "SF", "Seattle Mariners": "SEA",
  "St. Louis Cardinals": "STL", "Tampa Bay Rays": "TB", "Texas Rangers": "TEX",
  "Toronto Blue Jays": "TOR", "Washington Nationals": "WSH",
};

function teamAbbr(name) {
  if (!name) return "—";
  if (MLB_ABBREVS[name]) return MLB_ABBREVS[name];
  if (name.length <= 4) return name;
  return name.substring(0, 3).toUpperCase();
}

const MLB_TEAM_IDS = {
  ARI: 109, ATL: 144, BAL: 110, BOS: 111, CHC: 112, CWS: 145,
  CIN: 113, CLE: 114, COL: 115, DET: 116, HOU: 117, KC:  118,
  LAA: 108, LAD: 119, MIA: 146, MIL: 158, MIN: 142, NYM: 121,
  NYY: 147, ATH: 133, PHI: 143, PIT: 134, SD:  135, SF:  137,
  SEA: 136, STL: 138, TB:  139, TEX: 140, TOR: 141, WSH: 120,
};

function teamLogoUrl(teamName) {
  const id = MLB_TEAM_IDS[teamAbbr(teamName)];
  return id ? `https://www.mlbstatic.com/team-logos/${id}.svg` : null;
}

function fmtStat(val, decimals = 3) {
  if (val == null) return "—";
  return Number(val).toFixed(decimals);
}

export default function MlbHrDerbySwapPage() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);


  const [season,       setSeason]       = useState(null);
  const [myRosters,    setMyRosters]    = useState([]);
  const [allPlayers,   setAllPlayers]   = useState([]);
  const [loadingPage,  setLoadingPage]  = useState(true);
  const [pageError,    setPageError]    = useState("");

  // Selection state
  const [selectedRosterId,  setSelectedRosterId]  = useState(null);
  const [selectedRemoveId,  setSelectedRemoveId]  = useState(null); // player_id to remove

  // Confirmation modal
  const [addCandidate,  setAddCandidate]  = useState(null); // player object from allPlayers
  const [validating,    setValidating]    = useState(false);
  const [validateError, setValidateError] = useState("");
  const [validateOk,    setValidateOk]    = useState(null); // validation response
  const [applying,      setApplying]      = useState(false);
  const [applyError,    setApplyError]    = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Replacements table controls
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey,     setSortKey]     = useState("curr_hrs");
  const [sortDir,     setSortDir]     = useState("desc");

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function sortIndicator(key) {
    if (sortKey !== key) return " ↕";
    return sortDir === "desc" ? " ↓" : " ↑";
  }

  useEffect(() => {
    async function loadPage() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      try {
        const s = await getActiveSeason();
        setSeason(s);
        // The pool fetch's result is unused, but the call is kept: it rejects
        // for non-members, so it doubles as the page's access check.
        const [, rosters, players] = await Promise.all([
          getMlbPool(s.id, numericPoolId),
          getMyMlbRosters(s.id, numericPoolId),
          getMlbPlayers(s.id),
        ]);
        setMyRosters(rosters);
        setAllPlayers(players);
        // Auto-select first roster if only one
        if (rosters.length === 1) setSelectedRosterId(rosters[0].id);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    loadPage();
  }, [numericPoolId]);

  const selectedRoster = useMemo(
    () => myRosters.find((r) => r.id === selectedRosterId) || null,
    [myRosters, selectedRosterId]
  );

  const selectedRemovePlayer = useMemo(
    () => selectedRoster?.players?.find((p) => p.player_id === selectedRemoveId) || null,
    [selectedRoster, selectedRemoveId]
  );

  // Filter candidates: curr_hrs <= removed player's curr_hrs AND prev_hrs <= removed player's prev_hrs
  // Also exclude players already on the roster
  const rosterPlayerIds = useMemo(
    () => new Set((selectedRoster?.players || []).map((p) => p.player_id)),
    [selectedRoster]
  );

  const candidatePlayers = useMemo(() => {
    if (!selectedRemovePlayer) return [];
    const maxCurr = selectedRemovePlayer.curr_hrs;
    const maxPrev = selectedRemovePlayer.prev_hrs;
    return [...allPlayers]
      .filter((p) => {
        if (rosterPlayerIds.has(p.id)) return false;
        const curr = p.curr_season?.home_runs ?? 0;
        const prev = p.prev_season?.home_runs ?? 0;
        return curr <= maxCurr && prev <= maxPrev;
      })
      .sort((a, b) => {
        const bCurr = b.curr_season?.home_runs ?? 0;
        const aCurr = a.curr_season?.home_runs ?? 0;
        if (bCurr !== aCurr) return bCurr - aCurr;
        return (b.prev_season?.home_runs ?? 0) - (a.prev_season?.home_runs ?? 0);
      });
  }, [selectedRemovePlayer, allPlayers, rosterPlayerIds]);

  // Ranked with ties
  const candidatesRanked = useMemo(() => {
    let rank = 1;
    return candidatePlayers.map((p, i) => {
      const curr = p.curr_season?.home_runs ?? 0;
      if (i > 0 && curr < (candidatePlayers[i - 1].curr_season?.home_runs ?? 0)) rank = i + 1;
      return {
        id:           p.id,
        name:         p.name,
        team:         p.team,
        curr_hrs:     curr,
        prev_hrs:     p.prev_season?.home_runs ?? 0,
        games:        p.curr_season?.games ?? 0,
        at_bats:      p.curr_season?.at_bats ?? 0,
        batting_avg:  p.curr_season?.batting_avg ?? null,
        slugging_pct: p.curr_season?.slugging_pct ?? null,
        hr_rank:      rank,
      };
    });
  }, [candidatePlayers]);

  const displayedCandidates = useMemo(() => {
    let list = candidatesRanked;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || (p.team || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      const cmp = typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [candidatesRanked, searchQuery, sortKey, sortDir]);

  function handleSelectRemove(playerId) {
    setSelectedRemoveId((prev) => (prev === playerId ? null : playerId));
    setAddCandidate(null);
    setValidateError("");
    setValidateOk(null);
  }

  async function handleSelectAdd(player) {
    setAddCandidate(player);
    setValidateError("");
    setValidateOk(null);
    setValidating(true);
    onOpen();
    try {
      const result = await validateMlbSwap(season.id, numericPoolId, {
        roster_id:        selectedRosterId,
        remove_player_id: selectedRemoveId,
        add_player_id:    player.id,
      });
      setValidateOk(result);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Validation failed.";
      setValidateError(typeof msg === "string" ? msg : "Validation failed.");
    } finally {
      setValidating(false);
    }
  }

  async function handleConfirmSwap() {
    setApplying(true);
    setApplyError("");
    try {
      await applyMlbSwap(season.id, numericPoolId, {
        roster_id:        selectedRosterId,
        remove_player_id: selectedRemoveId,
        add_player_id:    addCandidate.id,
      });
      onClose();
      navigate(`/mlb-hr-derby/current/${numericPoolId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Swap failed.";
      setApplyError(typeof msg === "string" ? msg : "Swap failed.");
    } finally {
      setApplying(false);
    }
  }

  function handleModalClose() {
    if (applying) return;
    onClose();
    setAddCandidate(null);
    setValidateError("");
    setValidateOk(null);
    setApplyError("");
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading swap page...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">

      <PageHeader
        title="MLB HR DERBY"
        onBack={() => navigate("/mlb-hr-derby")}
        user={user}
        onLogout={handleLogout}
      />

      {/* ── Page Body ── */}
      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />{pageError}
          </Alert>
        )}

        {/* Action buttons */}
        <HStack mb={6} spacing={3}>
          <Button
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}`)}
            _hover={{ bg: "gray.100" }}
          >
            Leaderboard View
          </Button>
        </HStack>

        {/* ── Roster selector (only shown if user has multiple rosters) ── */}
        {myRosters.length > 1 && (
          <Box mb={6}>
            <Text color="whiteAlpha.800" fontWeight="semibold" mb={2}>Select a roster to manage:</Text>
            <HStack spacing={3} flexWrap="wrap">
              {myRosters.map((r) => (
                <Button
                  key={r.id}
                  onClick={() => {
                    setSelectedRosterId(r.id);
                    setSelectedRemoveId(null);
                    setAddCandidate(null);
                  }}
                  bg={selectedRosterId === r.id ? "white" : "whiteAlpha.200"}
                  color={selectedRosterId === r.id ? TOP_BAR_BLUE : "white"}
                  _hover={{ bg: selectedRosterId === r.id ? "gray.100" : "whiteAlpha.300" }}
                  fontWeight={selectedRosterId === r.id ? "bold" : "normal"}
                >
                  {r.team_name}
                  {r.swap_used && <Badge ml={2} colorScheme="purple" fontSize="2xs">SWAP USED</Badge>}
                </Button>
              ))}
            </HStack>
          </Box>
        )}

        {!selectedRoster ? (
          <Box bg="white" color="black" borderRadius="lg" p={8} textAlign="center">
            <Text color="gray.500">Select a roster above to manage your swap.</Text>
          </Box>
        ) : selectedRoster.swap_used ? (
          <Box bg="white" color="black" borderRadius="lg" p={8} textAlign="center">
            <Heading size="md" color={TOP_BAR_BLUE} mb={3}>{selectedRoster.team_name}</Heading>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              This roster has already used its one allowed swap for the season.
            </Alert>
          </Box>
        ) : (
          <HStack align="start" spacing={6} flexWrap="wrap">

            {/* LEFT — current roster, pick player to remove */}
            <Box
              flex="1"
              minW="280px"
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={6}
            >
              <Heading size="md" color={TOP_BAR_BLUE} mb={1}>{selectedRoster.team_name}</Heading>
              <Text fontSize="xs" color="gray.500" mb={4}>
                Click a player to select them for removal and see eligible replacements.
              </Text>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Player</Th>
                    <Th isNumeric>2025 HRs</Th>
                    <Th isNumeric>2026 HRs</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(selectedRoster.players || []).map((p) => {
                    const isSelected = p.player_id === selectedRemoveId;
                    return (
                      <Tr
                        key={p.player_id}
                        cursor="pointer"
                        bg={isSelected ? "red.50" : "transparent"}
                        _hover={{ bg: isSelected ? "red.100" : "gray.50" }}
                        onClick={() => handleSelectRemove(p.player_id)}
                        opacity={p.is_active ? 1 : 0.45}
                      >
                        <Td fontWeight={isSelected ? "bold" : "normal"}>
                          {p.name}
                          {!p.is_active && <Badge ml={2} colorScheme="gray" fontSize="2xs">OUT</Badge>}
                          {isSelected && <Badge ml={2} colorScheme="red" fontSize="2xs">REMOVE?</Badge>}
                        </Td>
                        <Td isNumeric>{p.prev_hrs}</Td>
                        <Td isNumeric fontWeight="bold">{p.curr_hrs}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            {/* RIGHT — eligible replacements */}
            <Box
              flex="3"
              minW="400px"
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={6}
            >
              {!selectedRemovePlayer ? (
                <Flex h="200px" align="center" justify="center">
                  <Text color="gray.400" fontSize="lg">← Select a player to remove to see eligible replacements</Text>
                </Flex>
              ) : (
                <>
                  <Heading size="md" color={TOP_BAR_BLUE} mb={1}>Eligible Replacements</Heading>
                  <Text fontSize="xs" color="gray.500" mb={3}>
                    Players with ≤ {selectedRemovePlayer.curr_hrs} HRs this season and ≤ {selectedRemovePlayer.prev_hrs} HRs last season.
                    Click a player to confirm the swap.
                  </Text>
                  <InputGroup mb={3} size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by name or team..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      bg="gray.50"
                      borderColor="gray.200"
                    />
                  </InputGroup>
                  <Box overflowX="auto" overflowY="auto" maxH="520px">
                    <Table size="sm" variant="simple">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th w="160px" cursor="pointer" onClick={() => toggleSort("name")} userSelect="none">
                            Player{sortIndicator("name")}
                          </Th>
                          <Th w="52px" cursor="pointer" onClick={() => toggleSort("team")} userSelect="none">
                            Team{sortIndicator("team")}
                          </Th>
                          <Th isNumeric w="52px" cursor="pointer" onClick={() => toggleSort("curr_hrs")} userSelect="none">
                            HRs{sortIndicator("curr_hrs")}
                          </Th>
                          <Th isNumeric w="72px" cursor="pointer" onClick={() => toggleSort("prev_hrs")} userSelect="none">
                            2025 HRs{sortIndicator("prev_hrs")}
                          </Th>
                          <Th isNumeric w="52px" cursor="pointer" onClick={() => toggleSort("games")} userSelect="none">
                            G{sortIndicator("games")}
                          </Th>
                          <Th isNumeric w="52px" cursor="pointer" onClick={() => toggleSort("at_bats")} userSelect="none">
                            AB{sortIndicator("at_bats")}
                          </Th>
                          <Th isNumeric w="64px" cursor="pointer" onClick={() => toggleSort("batting_avg")} userSelect="none">
                            AVG{sortIndicator("batting_avg")}
                          </Th>
                          <Th isNumeric w="64px" cursor="pointer" onClick={() => toggleSort("slugging_pct")} userSelect="none">
                            SLG{sortIndicator("slugging_pct")}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {displayedCandidates.length > 0 ? (
                          displayedCandidates.map((p) => (
                            <Tr
                              key={p.id}
                              cursor="pointer"
                              _hover={{ bg: "blue.50" }}
                              onClick={() => handleSelectAdd(p)}
                            >
                              <Td w="160px" fontWeight="medium">{p.name}</Td>
                              <Td>
                                <HStack spacing={1} align="center">
                                  {teamLogoUrl(p.team) && (
                                    <Image src={teamLogoUrl(p.team)} boxSize="18px" objectFit="contain" flexShrink={0} />
                                  )}
                                  <Text fontWeight="semibold" fontSize="sm">{teamAbbr(p.team)}</Text>
                                </HStack>
                              </Td>
                              <Td isNumeric fontWeight="bold">{p.curr_hrs}</Td>
                              <Td isNumeric>{p.prev_hrs}</Td>
                              <Td isNumeric>{p.games}</Td>
                              <Td isNumeric>{p.at_bats}</Td>
                              <Td isNumeric>{fmtStat(p.batting_avg)}</Td>
                              <Td isNumeric>{fmtStat(p.slugging_pct)}</Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={8}>
                              <Text color="gray.500">{searchQuery ? "No players match your search." : "No eligible players found."}</Text>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </Box>
          </HStack>
        )}
      </Container>

      {/* ── Confirm Swap Modal ── */}
      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size="md" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
          <ModalHeader color={TOP_BAR_BLUE}>Confirm Swap</ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} isDisabled={applying} />
          <ModalBody>
            {validating ? (
              <Flex align="center" justify="center" py={8} direction="column" gap={3}>
                <Spinner size="lg" />
                <Text>Validating swap...</Text>
              </Flex>
            ) : validateError ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />{validateError}
              </Alert>
            ) : validateOk ? (
              <VStack align="stretch" spacing={4}>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />This swap is valid.
                </Alert>
                <Table size="sm" variant="simple">
                  <Tbody>
                    <Tr>
                      <Td color="red.600" fontWeight="bold">Remove</Td>
                      <Td>{selectedRemovePlayer?.name}</Td>
                      <Td isNumeric>{selectedRemovePlayer?.curr_hrs} HRs</Td>
                    </Tr>
                    <Tr>
                      <Td color="green.600" fontWeight="bold">Add</Td>
                      <Td>{addCandidate?.name}</Td>
                      <Td isNumeric>{addCandidate?.curr_hrs} HRs</Td>
                    </Tr>
                  </Tbody>
                </Table>
                <Text fontSize="xs" color="gray.500">
                  This is your one allowed swap for this roster. This action cannot be undone.
                </Text>
                {applyError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />{applyError}
                  </Alert>
                )}
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleModalClose} isDisabled={applying}>
                Cancel
              </Button>
              {validateOk && !validateError && (
                <Button
                  bg="red.600"
                  color="white"
                  _hover={{ bg: "red.700" }}
                  onClick={handleConfirmSwap}
                  isLoading={applying}
                  loadingText="Applying..."
                >
                  Confirm Swap
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
